// Superadmin «Barn»: typer og rene hjelpere for redigering av læringsstien for
// barn (GET/PUT /api/v1/admin/kids/...). Komponentene ligger i KidsPage.tsx.

export const KIDS_API = "/api/v1/admin/kids";

export type KidsItemType = "DUA" | "SURAH" | "LEARN" | "ADHAN";
export const ITEM_TYPES: KidsItemType[] = ["DUA", "SURAH", "LEARN", "ADHAN"];
export const TYPE_LABELS: Record<KidsItemType, string> = {
  DUA: "Dua",
  SURAH: "Sura",
  LEARN: "Lær å be",
  ADHAN: "Adhan",
};
export const LEVEL_ICONS = ["star", "sun", "prayer", "heart"] as const;
export const LEVEL_COLORS = ["mint", "gold", "blue", "rose"] as const;
export const LEARN_KEYS = ["wudu", "salah"] as const;
export const ICON_EMOJI: Record<string, string> = {
  star: "⭐",
  sun: "☀️",
  prayer: "🤲",
  heart: "💛",
};
export const ICON_LABELS: Record<string, string> = {
  star: "Stjerne",
  sun: "Sol",
  prayer: "Bønn",
  heart: "Hjerte",
};
export const COLOR_LABELS: Record<string, string> = {
  mint: "Mint",
  gold: "Gull",
  blue: "Blå",
  rose: "Rosa",
};
export const LEARN_LABELS: Record<string, string> = {
  wudu: "Wudu",
  salah: "Bønnens steg",
};

export const MAX_AUDIO_BYTES = 10 * 1024 * 1024;
export const AUDIO_ACCEPT =
  ".mp3,.m4a,.aac,audio/mpeg,audio/mp4,audio/x-m4a,audio/aac";

export interface AdhanPhrase {
  arabic: string;
  transliteration?: string | null;
  meaningNb?: string | null;
  meaningEn?: string | null;
  meaningUr?: string | null;
  repeat: number;
  audioUrl?: string | null;
  fajrOnly: boolean;
}

export interface KidsDua {
  id: number;
  kategori?: string | null;
  tittel?: string | null;
  arabisk?: string | null;
  arabisk1?: string | null;
  transliteration?: string | null;
  oversettelse?: string | null;
  engelsk?: string | null;
  urdu?: string | null;
  referanse?: string | null;
  audioId?: number | null;
  audioUrl?: string | null;
}

/** Stasjonen slik appene får den (GET /api/v1/kids/curriculum). */
export interface PublicItem {
  id: string;
  type: KidsItemType;
  audioUrl?: string | null;
  dua?: KidsDua | null;
  phrases?: AdhanPhrase[] | null;
}

export interface AdminLevel {
  id: string;
  sortOrder: number;
  title: string;
  titleEn?: string | null;
  titleUr?: string | null;
  titleAr?: string | null;
  icon?: string | null;
  color?: string | null;
  active: boolean;
  itemCount: number;
}

export interface AdminItem {
  id: string;
  levelId: string;
  sortOrder: number;
  type: KidsItemType;
  active: boolean;
  duaId?: number | null;
  duaTitle?: string | null;
  duaCategory?: string | null;
  surahNumber?: number | null;
  learnKey?: string | null;
  kidTitle: string;
  kidTitleEn?: string | null;
  kidTitleUr?: string | null;
  kidTitleAr?: string | null;
  kidText?: string | null;
  kidTextEn?: string | null;
  kidTextUr?: string | null;
  kidTextAr?: string | null;
  emoji?: string | null;
  stars: number;
  arabicOverride?: string | null;
  transliterationOverride?: string | null;
  meaningOverrideNb?: string | null;
  meaningOverrideEn?: string | null;
  meaningOverrideUr?: string | null;
  audioUrlOverride?: string | null;
  phrases: AdhanPhrase[];
  dua?: KidsDua | null;
  preview?: PublicItem | null;
  hiddenReason?: string | null;
}

export interface AdminCurriculum {
  levels: AdminLevel[];
  items: AdminItem[];
  defaultAdhanAudioUrl?: string | null;
}

export interface CopySummary {
  copied: string[];
  skipped: Array<{ id: string; reason: string }>;
  failed: Array<{ id: string; reason: string }>;
}

/** Skjemaet for en stasjon. Tekstfelter er alltid strenger (tom = ikke satt). */
export interface ItemForm {
  id: string;
  levelId: string;
  type: KidsItemType;
  active: boolean;
  duaId: number | null;
  surahNumber: string;
  learnKey: string;
  kidTitle: string;
  kidTitleEn: string;
  kidTitleUr: string;
  kidTitleAr: string;
  kidText: string;
  kidTextEn: string;
  kidTextUr: string;
  kidTextAr: string;
  emoji: string;
  stars: number;
  arabicOverride: string;
  transliterationOverride: string;
  meaningOverrideNb: string;
  meaningOverrideEn: string;
  meaningOverrideUr: string;
  audioUrlOverride: string;
  phrases: AdhanPhrase[];
}

export interface LevelForm {
  id: string;
  title: string;
  titleEn: string;
  titleUr: string;
  titleAr: string;
  icon: string;
  color: string;
  active: boolean;
}

const s = (v?: string | null) => v ?? "";
const orNull = (v: string) => (v.trim() ? v.trim() : null);

export const emptyPhrase = (): AdhanPhrase => ({
  arabic: "",
  transliteration: "",
  meaningNb: "",
  meaningEn: "",
  meaningUr: "",
  repeat: 2,
  audioUrl: null,
  fajrOnly: false,
});

export const newItemForm = (levelId: string): ItemForm => ({
  id: "",
  levelId,
  type: "DUA",
  active: true,
  duaId: null,
  surahNumber: "",
  learnKey: "",
  kidTitle: "",
  kidTitleEn: "",
  kidTitleUr: "",
  kidTitleAr: "",
  kidText: "",
  kidTextEn: "",
  kidTextUr: "",
  kidTextAr: "",
  emoji: "",
  stars: 1,
  arabicOverride: "",
  transliterationOverride: "",
  meaningOverrideNb: "",
  meaningOverrideEn: "",
  meaningOverrideUr: "",
  audioUrlOverride: "",
  phrases: [],
});

export const itemToForm = (item: AdminItem): ItemForm => ({
  id: item.id,
  levelId: item.levelId,
  type: item.type,
  active: item.active,
  // Stasjoner fra migrasjonen kan mangle dua_id: bruk duaen backend fant.
  duaId: item.duaId ?? item.dua?.id ?? null,
  surahNumber: item.surahNumber != null ? String(item.surahNumber) : "",
  learnKey: s(item.learnKey),
  kidTitle: item.kidTitle,
  kidTitleEn: s(item.kidTitleEn),
  kidTitleUr: s(item.kidTitleUr),
  kidTitleAr: s(item.kidTitleAr),
  kidText: s(item.kidText),
  kidTextEn: s(item.kidTextEn),
  kidTextUr: s(item.kidTextUr),
  kidTextAr: s(item.kidTextAr),
  emoji: s(item.emoji),
  stars: item.stars,
  arabicOverride: s(item.arabicOverride),
  transliterationOverride: s(item.transliterationOverride),
  meaningOverrideNb: s(item.meaningOverrideNb),
  meaningOverrideEn: s(item.meaningOverrideEn),
  meaningOverrideUr: s(item.meaningOverrideUr),
  audioUrlOverride: s(item.audioUrlOverride),
  phrases: item.phrases.map((p) => ({
    ...p,
    transliteration: s(p.transliteration),
    meaningNb: s(p.meaningNb),
    meaningEn: s(p.meaningEn),
    meaningUr: s(p.meaningUr),
    audioUrl: p.audioUrl ?? null,
  })),
});

/** Body til POST/PUT /items. Felter som ikke gjelder typen sendes ikke. */
export const formToRequest = (form: ItemForm, isNew: boolean) => ({
  ...(isNew && form.id.trim() ? { id: form.id.trim() } : {}),
  levelId: form.levelId,
  type: form.type,
  active: form.active,
  kidTitle: form.kidTitle.trim(),
  kidTitleEn: orNull(form.kidTitleEn),
  kidTitleUr: orNull(form.kidTitleUr),
  kidTitleAr: orNull(form.kidTitleAr),
  kidText: orNull(form.kidText),
  kidTextEn: orNull(form.kidTextEn),
  kidTextUr: orNull(form.kidTextUr),
  kidTextAr: orNull(form.kidTextAr),
  emoji: orNull(form.emoji),
  stars: form.stars,
  audioUrlOverride: orNull(form.audioUrlOverride),
  ...(form.type === "DUA"
    ? {
        duaId: form.duaId,
        arabicOverride: orNull(form.arabicOverride),
        transliterationOverride: orNull(form.transliterationOverride),
        meaningOverrideNb: orNull(form.meaningOverrideNb),
        meaningOverrideEn: orNull(form.meaningOverrideEn),
        meaningOverrideUr: orNull(form.meaningOverrideUr),
      }
    : {}),
  ...(form.type === "SURAH"
    ? { surahNumber: form.surahNumber ? Number(form.surahNumber) : null }
    : {}),
  ...(form.type === "LEARN" ? { learnKey: form.learnKey || null } : {}),
  ...(form.type === "ADHAN"
    ? {
        phrases: form.phrases.map((p) => ({
          arabic: p.arabic.trim(),
          transliteration: orNull(s(p.transliteration)),
          meaningNb: orNull(s(p.meaningNb)),
          meaningEn: orNull(s(p.meaningEn)),
          meaningUr: orNull(s(p.meaningUr)),
          repeat: p.repeat,
          audioUrl: p.audioUrl || null,
          fajrOnly: p.fajrOnly,
        })),
      }
    : {}),
});

export const newLevelForm = (): LevelForm => ({
  id: "",
  title: "",
  titleEn: "",
  titleUr: "",
  titleAr: "",
  icon: "star",
  color: "mint",
  active: true,
});

export const levelToForm = (level: AdminLevel): LevelForm => ({
  id: level.id,
  title: level.title,
  titleEn: s(level.titleEn),
  titleUr: s(level.titleUr),
  titleAr: s(level.titleAr),
  icon: s(level.icon) || "star",
  color: s(level.color) || "mint",
  active: level.active,
});

export const levelFormToRequest = (form: LevelForm, isNew: boolean) => ({
  ...(isNew && form.id.trim() ? { id: form.id.trim() } : {}),
  title: form.title.trim(),
  titleEn: orNull(form.titleEn),
  titleUr: orNull(form.titleUr),
  titleAr: orNull(form.titleAr),
  icon: form.icon,
  color: form.color,
  active: form.active,
});

/** Om skjemaet er endret siden det ble åpnet/lagret. */
export const isDirty = <T>(initial: T, current: T) =>
  JSON.stringify(initial) !== JSON.stringify(current);

/** Feil i skjemaet før det sendes (serveren sjekker også). Tom liste = ok. */
export const validateItemForm = (form: ItemForm): string[] => {
  const errors: string[] = [];
  if (!form.kidTitle.trim()) errors.push("Tittel for barn mangler.");
  if (form.kidTitle.trim().length > 80)
    errors.push("Tittelen kan være maks 80 tegn.");
  if (form.kidText.trim().length > 300)
    errors.push("Teksten kan være maks 300 tegn.");
  if (!form.levelId) errors.push("Velg et nivå.");
  if (form.type === "DUA" && form.duaId == null) errors.push("Velg en dua.");
  if (form.type === "SURAH") {
    const n = Number(form.surahNumber);
    if (!Number.isInteger(n) || n < 1 || n > 114)
      errors.push("Surahnummer må være 1–114.");
  }
  if (form.type === "LEARN" && !form.learnKey)
    errors.push("Velg hva barnet skal lære.");
  if (form.type === "ADHAN") {
    if (!form.phrases.length) errors.push("Adhan trenger minst én frase.");
    form.phrases.forEach((p, i) => {
      if (!p.arabic.trim())
        errors.push(`Frase ${i + 1} mangler arabisk tekst.`);
      if (p.repeat < 1 || p.repeat > 10)
        errors.push(`Frase ${i + 1}: antall ganger må være 1–10.`);
    });
  }
  if (form.stars < 0 || form.stars > 10) errors.push("Stjerner må være 0–10.");
  return errors;
};

/** Sjekker en lydfil før opplasting. Gir en feilmelding, eller null når den er ok. */
export const audioFileError = (file: {
  name: string;
  size: number;
  type: string;
}) => {
  const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
  if (!["mp3", "m4a", "aac"].includes(ext))
    return "Bruk en lydfil i mp3, m4a eller aac.";
  if (file.size === 0) return "Filen er tom.";
  if (file.size > MAX_AUDIO_BYTES) return "Lydfilen kan være maks 10 MB.";
  return null;
};

/** Flytter et element i en liste (dra og slipp, eller opp/ned). */
export const move = <T>(list: T[], from: number, to: number): T[] => {
  if (from === to || from < 0 || from >= list.length) return list;
  const next = [...list];
  const [item] = next.splice(from, 1);
  next.splice(Math.max(0, Math.min(to, next.length)), 0, item);
  return next;
};

/**
 * Ny rekkefølge når stasjonen `itemId` slippes i `levelId` foran `beforeId`
 * (eller sist når `beforeId` mangler). Gir { levelId: [id] } for PUT /order.
 */
export const dropItem = (
  order: Record<string, string[]>,
  itemId: string,
  levelId: string,
  beforeId: string | null,
): Record<string, string[]> => {
  const next: Record<string, string[]> = {};
  Object.entries(order).forEach(([l, ids]) => {
    next[l] = ids.filter((id) => id !== itemId);
  });
  const target = next[levelId] ?? [];
  const at = beforeId ? target.indexOf(beforeId) : -1;
  next[levelId] =
    at >= 0
      ? [...target.slice(0, at), itemId, ...target.slice(at)]
      : [...target, itemId];
  return next;
};

/** Stasjons-id-ene per nivå i nåværende rekkefølge. */
export const itemOrder = (levels: AdminLevel[], items: AdminItem[]) => {
  const order: Record<string, string[]> = {};
  levels.forEach((l) => {
    order[l.id] = items
      .filter((i) => i.levelId === l.id)
      .sort((a, b) => a.sortOrder - b.sortOrder || a.id.localeCompare(b.id))
      .map((i) => i.id);
  });
  return order;
};

export type PreviewLang = "nb" | "en" | "ur" | "ar";

export interface PreviewCard {
  title: string;
  text: string;
  arabic: string;
  transliteration: string;
  meaning: string;
  audioUrl: string | null;
  /** «Barnets egen lyd», «Duaens lyd» osv. */
  audioSource: string;
  reference: string;
}

const pick = (
  lang: PreviewLang,
  nb: string,
  en: string,
  ur: string,
  ar: string,
) => (lang === "en" ? en : lang === "ur" ? ur : lang === "ar" ? ar : nb) || nb;

/**
 * Lærekortet slik appen viser det, regnet ut fra skjemaet (før lagring) og den
 * valgte duaen. Samme regler som backend: overstyringene vinner over duaen.
 */
export const previewCard = (
  form: ItemForm,
  dua: KidsDua | null,
  lang: PreviewLang,
  defaultAdhanAudioUrl?: string | null,
): PreviewCard => {
  const own = form.audioUrlOverride.trim();
  const title = pick(
    lang,
    form.kidTitle,
    form.kidTitleEn,
    form.kidTitleUr,
    form.kidTitleAr,
  );
  const text = pick(
    lang,
    form.kidText,
    form.kidTextEn,
    form.kidTextUr,
    form.kidTextAr,
  );
  if (form.type === "DUA") {
    const meaningNb = form.meaningOverrideNb.trim() || s(dua?.oversettelse);
    const meaningEn = form.meaningOverrideEn.trim() || s(dua?.engelsk);
    const meaningUr = form.meaningOverrideUr.trim() || s(dua?.urdu);
    return {
      title,
      text,
      arabic: form.arabicOverride.trim() || s(dua?.arabisk1) || s(dua?.arabisk),
      transliteration:
        form.transliterationOverride.trim() || s(dua?.transliteration),
      meaning:
        lang === "en"
          ? meaningEn || meaningNb
          : lang === "ur"
            ? meaningUr || meaningEn || meaningNb
            : meaningNb,
      audioUrl: own || dua?.audioUrl || null,
      audioSource: own
        ? "Barnets egen lyd"
        : dua?.audioUrl
          ? "Duaens lyd (felles fil)"
          : "Ingen lyd",
      reference: s(dua?.referanse),
    };
  }
  const fallback = form.type === "ADHAN" ? defaultAdhanAudioUrl || null : null;
  return {
    title,
    text,
    arabic: "",
    transliteration: "",
    meaning: "",
    audioUrl: own || fallback,
    audioSource: own
      ? "Barnets egen lyd"
      : fallback
        ? "Standard adhan fra adhan-listen"
        : form.type === "ADHAN"
          ? "Appens innebygde adhan"
          : "Appens vanlige lyd",
    reference: "",
  };
};

/** Betydningen av en adhan-frase på valgt språk (engelsk som reserve). */
export const phraseMeaning = (p: AdhanPhrase, lang: PreviewLang) =>
  (lang === "en"
    ? p.meaningEn
    : lang === "ur"
      ? p.meaningUr || p.meaningEn
      : lang === "ar"
        ? p.meaningEn
        : p.meaningNb) ||
  p.meaningNb ||
  "";

/** Feilmeldingen fra serveren ({ message } / { detail }), ellers en standardtekst. */
export const errorText = (
  error: { code: number; message?: string } | null,
  fallback: string,
) => (error?.message ? error.message : fallback);
